
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LOGO_SVG } from '../constants';
import { UserProfile, AppearanceMode } from '../types';

interface LayoutProps {
  children: React.ReactNode;
  user: UserProfile;
  onUpdateUser: (updates: Partial<UserProfile>) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onUpdateUser }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const root = window.document.documentElement;
    const mode = user.appearanceMode;
    
    if (mode === AppearanceMode.AUTO) {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', systemDark);
    } else {
      root.classList.toggle('dark', mode === AppearanceMode.DARK);
    }
  }, [user.appearanceMode]);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar_collapsed', String(newState));
  };

  const navItems = [
    { label: 'Dashboard', path: '/app/dashboard', icon: '📊' },
    { label: 'AI Tools', path: '/app/tools', icon: '🧩' },
    { label: 'Automations', path: '/app/automations', icon: '⚙️' },
    { label: 'History', path: '/app/history', icon: '📜' },
    { label: 'Billing', path: '/app/billing', icon: '💳' },
    { label: 'Settings', path: '/app/settings', icon: '🛠️' },
  ];

  // Logic to determine a friendly page title in the top bar
  const getHeaderTitle = () => {
    const path = location.pathname;
    // Explicitly handle tool detail pages to avoid showing internal IDs like "aa1"
    if (path.match(/\/app\/tools\/[a-zA-Z0-9]+/)) return 'Tool Details';
    
    const segments = path.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (!last || last === 'app') return 'Dashboard';
    
    // Capitalize and format friendly names
    return last.replace(/-/g, ' ');
  };

  const SidebarContent = () => (
    <div className={`h-full flex flex-col transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'}`}>
      <div className={`p-6 flex items-center transition-all ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
        {LOGO_SVG}
        {!isCollapsed && <span className="font-bold text-xl tracking-tight dark:text-white text-slate-900">MarketXtractor</span>}
      </div>
      
      <nav className="flex-1 px-3 space-y-1 py-4">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            title={isCollapsed ? item.label : ''}
            onClick={() => setIsMobileMenuOpen(false)}
            className={`flex items-center rounded-lg transition-all px-4 py-3 group ${
              location.pathname.startsWith(item.path)
                ? 'bg-indigo-600 text-white font-medium shadow-lg'
                : 'hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-slate-100'
            } ${isCollapsed ? 'justify-center' : 'space-x-4'}`}
          >
            <span className="text-xl shrink-0">{item.icon}</span>
            {!isCollapsed && <span className="truncate">{item.label}</span>}
          </Link>
        ))}
      </nav>

      <div className={`p-4 border-t dark:border-slate-800 border-slate-200 bg-slate-100/50 dark:bg-slate-900/40`}>
        {!isCollapsed && (
          <div className="mb-4">
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-2">My Plan: {user.plan}</div>
            <div className="w-full bg-slate-300 dark:bg-slate-800 rounded-full h-1.5 mb-1">
              <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (user.creditsRemaining / 20000) * 100)}%` }}></div>
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 font-bold">
              <span>{user.creditsRemaining.toLocaleString()} CREDITS</span>
            </div>
          </div>
        )}

        <div className="flex flex-col space-y-2">
          <button 
            onClick={() => {
              const nextMode = user.appearanceMode === AppearanceMode.DARK ? AppearanceMode.LIGHT : AppearanceMode.DARK;
              onUpdateUser({ appearanceMode: nextMode });
            }}
            className={`w-full flex items-center px-3 py-2 rounded-lg text-xs font-bold transition-all bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
            title="Toggle Appearance"
          >
            <span>{user.appearanceMode === AppearanceMode.DARK ? '☀️' : '🌙'}</span>
            {!isCollapsed && <span>{user.appearanceMode === AppearanceMode.DARK ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>
          <button 
            onClick={toggleSidebar}
            className={`hidden md:flex w-full items-center px-3 py-2 rounded-lg text-xs font-bold transition-all bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <span>{isCollapsed ? '➡️' : '⬅️'}</span>
            {!isCollapsed && <span>Collapse Sidebar</span>}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden transition-colors duration-300 dark:bg-slate-950 bg-slate-50 text-slate-900 dark:text-slate-100">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:block transition-all duration-300 border-r dark:border-slate-800 border-slate-200 dark:bg-slate-950/50 bg-white`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 md:hidden bg-white dark:bg-slate-950 border-r dark:border-slate-800 border-slate-200 transition-transform duration-300 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="w-64 h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Main Viewport */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <header className={`h-16 border-b flex items-center justify-between px-4 md:px-8 z-10 transition-colors dark:bg-slate-950/80 dark:border-slate-800 border-slate-200 bg-white/80 backdrop-blur-md`}>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              ☰
            </button>
            <h1 className="text-lg font-bold capitalize text-mx-text-primary">
              {getHeaderTitle()}
            </h1>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-600 dark:text-indigo-400">
              <span className="text-[10px] font-black">{user.creditsRemaining.toLocaleString()} CREDITS</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right hidden sm:block">
                <div className="text-sm font-bold">{user.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-tighter">{user.role}</div>
              </div>
              <Link to="/app/settings" className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold border-2 dark:border-slate-800 border-slate-200 shadow-lg hover:scale-105 transition-transform">
                {user.name.charAt(0)}
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8 relative">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
